import { useEffect } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from '@tanstack/react-router';
import type { SubmitErrorHandler, SubmitHandler } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { z } from 'zod';

import { APICommunicator } from '../../../api/APICommunicator';
import { schemaEmail, type SchemaEmail } from '../schemas/email';
import TreatButton from '~/components/TreatButton';

export default function RequestPasswordRoute() {
    const { t } = useTranslation();
    const { handleSubmit, register } = useForm<SchemaEmail>({
        defaultValues: {
            email: '',
        },
        resolver: zodResolver(schemaEmail),
    });

    const navigate = useNavigate({ from: '/reset/password' });

    useEffect(() => {
        (async () => {
            if (await APICommunicator.renew()) navigate({ to: '/' });
        })();
    }, [navigate]);

    const onSubmit: SubmitHandler<SchemaEmail> = async ({ email }) => {
        const response = await APICommunicator.resetResource({
            config: {
                body: JSON.stringify({ email }),
                method: 'POST',
            },
            path: '/request/password',
            schema: z.object({
                success: z.boolean(),
            }),
        });
        if (response.success) {
            toast.success(t('LOGIN.PASSWORD_RESET_EMAIL_SENT'), { theme: 'colored' });
        } else {
            toast.error(t('LOGIN.PASSWORD_RESET_EMAIL_FAILED'), { theme: 'colored' });
        }
    };

    const onError: SubmitErrorHandler<SchemaEmail> = (error) => {
        if (error.email) {
            toast.error(t('LOGIN.EMAIL_NOT_VALID'), { theme: 'colored' });
        }
    };

    return (
        <div className="flex items-center justify-center w-full min-h-screen p-4">
            <div className="w-full max-w-[420px] p-4 mx-auto bg-grey-light">
                <h3 className="mb-4 text-3xl font-bold text-center text-blue">{t('PAGES.LOGIN')}</h3>
                <form key="checkUser" onSubmit={handleSubmit(onSubmit, onError)}>
                    <input
                        {...register('email')}
                        type="text"
                        className="bg-white form-input border-transparent w-full placeholder:text-dark text-grey text-xs px-3 py-2.5 mb-4"
                        placeholder={t('LOGIN.PLACEHOLDER_EMAIL')}
                        aria-label={t('LOGIN.EMAIL_LABEL')}
                    />
                    <div className="flex items-center justify-end space-x-5">
                        <TreatButton className="text-xs font-semibold bg-blue py-2 px-3.5 text-white">
                            {t('LOGIN.SEND_RESET_PASSWORD')}
                        </TreatButton>
                    </div>
                </form>
            </div>
        </div>
    );
}
